<template>
  <div
    ref="parent"
    :class="[
      'core-scrollbar',
      {
        'is-focused': hasFocus,
        'is-valid': success,
        'has-value': hasValue,
        'has-error': error,
        'has-warning': warning,
        'is-disabled': disabled,
        'maz-is-dark': dark,
        'has-hint': hint,
        'has-no-label': !hasLabel && !hint,
        'has-left-icon': hasLeftIcon(),
      },
      size,
      `has-${leftNumberIcon}-right-icon`,
      `maz-input--${color}`,
    ]"
    class="maz-base-component maz-input"
    @click="focusInput"
  >
    <div
      v-if="hasLeftIcon()"
      class="maz-input__icon maz-flex left"
      :class="[textarea ? 'maz-align-start maz-pt-2' : 'maz-align-center']"
      :targetIcon="leftIconName"
      @click="iconclick"
    >
      <!-- Icon slot (`icon-left`) -->
      <slot :name="`icon-left`">
        <!-- none -->
        <i class="material-icons">{{ leftIconName }}</i>
      </slot>
    </div>

    <div
      v-if="hasRightIcon()"
      class="maz-input__icon maz-flex right"
      :class="[textarea ? 'maz-align-start maz-pt-2' : 'maz-align-center']"
      :targetIcon="rightIconName"
      @click="rightIconClick"
    >
      <!-- Icon slot (`icon-right`) -->
      <slot :name="`icon-right`">
        <!-- none -->
        <i class="material-icons">{{ rightIconName }}</i>
      </slot>
    </div>
    <textarea
      v-if="textarea && !editable"
      :id="uniqueId"
      ref="mazInput"
      v-model="inputValue"
      v-bind="$attrs"
      :placeholder="placeholderValue"
      :type="type"
      :required="required"
      :readonly="readonly"
      :disabled="disabled"
      class="maz-input__input maz-textarea maz-border maz-border-color maz-border-color-hover maz-border-solid maz-h-100"
      @keydown="keyDown"
      @keyup="keyUp"
      @focus="onFocus"
      @blur="onBlur"
      @paste="onPaste"
      @drop="onDrop"
      @change="onChange"
      @click="$emit('click', $event)"
    />
    <div
      v-else-if="!textarea && editable"
      class="editable-input maz-input__input maz-textarea maz-border maz-border-color maz-border-color-hover maz-border-solid maz-h-100"
      :contenteditable="!disabled"
      :id="uniqueId"
      ref="mazInput"
      v-bind="$attrs"
      :data-ph="placeholderValue"
      @keydown="keyDown"
      @keyup="keyUp"
      @input="onChange"
      @focus="onFocus"
      @blur="onBlur"
      @paste="onPaste"
      @drop="onDrop"
      @click="$emit('click', $event)"
    />
    <input
      v-else
      :id="uniqueId"
      ref="mazInput"
      v-model="inputValue"
      v-bind="{
        ...(getType === 'number' && { step: 'any', min: '0' }),
        ...$attrs,
      }"
      :placeholder="placeholderValue"
      :type="getType"
      class="maz-input__input maz-border maz-border-color maz-border-color-hover maz-border-solid"
      :aria-label="placeholder"
      :class="{
        'has-right-icon': hasClearBtn || hasPasswordBtn || hasRightIcon(),
      }"
      :disabled="disabled"
      :required="required"
      :readonly="readonly"
      @keydown="keyDown"
      @keyup="keyUp"
      @focus="onFocus"
      @blur="onBlur"
      @paste="onPaste"
      @drop="onDrop"
      @change="onChange"
      @click="$emit('click', $event)"
    />
    <label
      v-if="(hasLabel || hint) && !editable"
      ref="label"
      :for="uniqueId"
      :class="{
        'label-textarea': textarea,
        'label-textarea-disabled': disabled,
        'maz-text-danger': error,
      }"
      class="maz-input__label"
      tabindex="-1"
      @click="focusInput"
      style="pointer-events: none"
    >
      {{ hintValue || placeholderValue }}
    </label>
    <transition-group name="maz-scale">
      <button
        v-if="hasClearBtn"
        key="clear-button"
        class="maz-input__toggle-btn --clear maz-flex maz-flex-center"
        title="clear"
        type="button"
        tabindex="-1"
        :disabled="disabled"
        :class="{ 'has-right-icon': hasRightIcon(), disabled: disabled }"
        @click.stop="clear"
      >
        <i v-if="!disabled" class="maz-input__toggle-btn__icon material-icons">
          close
        </i>
      </button>

      <button
        v-if="hasPasswordBtn"
        key="password-button"
        class="maz-input__toggle-btn password maz-flex maz-flex-center"
        :class="{
          'has-clear-btn': hasClearBtn,
          'has-right-icon': hasRightIcon(),
        }"
        title="clear"
        type="button"
        tabindex="-1"
        @click="showPassword = !showPassword"
      >
        <simple-svg
          :filepath="passwordIconSrc"
          height="20"
          class="password-icon maz-input__toggle-btn__icon material-icons"
        />
      </button>
    </transition-group>

    <div v-if="loading" class="maz-input__loader" :class="{ textarea }">
      <div class="maz-input__loader__progress-bar" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CoreInput',
  props: {
    value: {
      type: [String, Number],
      default: null,
      validator: (prop) =>
        ['string', 'number'].includes(typeof prop) || prop === null
    },
    id: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'Enter text'
    },
    hint: {
      type: String,
      default: null
    },
    size: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'text'
    },
    leftIconName: {
      type: String,
      default: null
    },
    rightIconName: {
      type: String,
      default: null
    },
    error: {
      type: Boolean,
      default: false
    },
    warning: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    success: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    textarea: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    noLabel: {
      type: Boolean,
      default: false
    },
    noRequiredSymbol: {
      type: Boolean,
      default: false
    },
    focus: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'primary'
    }
  },
  emits: [
    'input',
    'focus',
    'blur',
    'paste',
    'drop',
    'change',
    'clear',
    'click',
    'keyup',
    'keydown',
    'iconclick',
    'rightIconClick'
  ],
  data () {
    return {
      isFocus: !!this.focus,
      showPassword: false,
      uid: '',
      uniqueId: ''
    }
  },
  computed: {
    inputValue: {
      get () {
        return this.value
      },
      set (value) {
        const inputValue = this.hasNumberType
          ? !value
            ? 0
            : parseFloat(String(value))
          : value
        this.$emit('input', inputValue)
      }
    },
    placeholderValue () {
      return this.required &&
        this.placeholder &&
        !this.noRequiredSymbol &&
        !this.placeholder.endsWith(' *')
        ? this.placeholder.concat(' *')
        : this.placeholder
    },
    hintValue () {
      return this.required && this.hint && !this.hint.endsWith(' *')
        ? this.hint.concat(' *')
        : this.hint
    },
    hasNumberType () {
      return this.type === 'number'
    },
    hasLabel () {
      return !this.noLabel
    },
    getType () {
      return this.showPassword ? 'text' : this.type
    },
    hasPasswordBtn () {
      return this.type === 'password' && this.inputValue
    },
    hasClearBtn () {
      return (
        this.clearable && this.inputValue && !this.disabled && !this.textarea
      )
    },
    hasValue () {
      switch (typeof this.value) {
        case 'string':
          return !!this.value
        case 'number':
          return true
        default:
          return false
      }
    },
    leftNumberIcon () {
      const icons =
        [this.hasRightIcon(), this.hasClearBtn, this.hasPasswordBtn] || []
      return icons.filter((icon) => icon).length
    },
    hasFocus () {
      return this.isFocus || this.focus
    },
    passwordIconSrc () {
      if (this.showPassword) {
        return require('../../assets/icons/visibility_off.svg')
      } else {
        return require('../../assets/icons/visibility.svg')
      }
    }
  },
  methods: {
    hasLeftIcon () {
      return this.leftIconName || this.$slots['icon-left']
    },
    hasRightIcon () {
      return this.rightIconName || this.$slots['icon-right']
    },
    focusInput () {
      this.$refs.mazInput.focus()
    },
    onFocus (e) {
      this.$emit('focus', e)
      this.isFocus = true
    },
    onBlur (e) {
      this.$emit('blur', e)
      this.isFocus = false
    },
    onPaste (e) {
      this.$emit('paste', e)
    },
    onDrop (e) {
      this.$emit('drop', e)
    },
    onChange (e) {
      this.$emit('change', e)
    },
    clear () {
      this.$emit('input', this.hasNumberType ? 0 : '')
      this.$emit('clear')
    },
    keyUp (e) {
      if (e.code === 'Escape') {
        if (this.$refs.mazInput.textarea !== undefined) {
          this.$refs.mazInput.textarea = ''
        }
        if (this.$refs.mazInput.value !== undefined) {
          this.$refs.mazInput.value = ''
          this.clear()
        }
        if (this.$refs.mazInput.innerHTML !== undefined) {
          this.$refs.mazInput.innerHTML = ''
        }
      }
      this.$emit('keyup', e)
    },
    keyDown (e) {
      if (e.keyCode === 33) {
        e.preventDefault()
      } else if (e.keyCode === 34) {
        e.preventDefault()
      }
      this.$emit('keydown', e)
    },
    iconclick () {
      this.$emit('iconclick')
    },
    rightIconClick () {
      this.$emit('rightIconClick')
    }
  },
  mounted () {
    const id = this.$attrs.id
    this.uniqueId = id ? `${id}` : `${this.$options.name}-${this.uid}`
  }
}
</script>

<style lang="scss" scoped>
@import "src/styles/_variables";

input {
  font-size: 12px;
  font-weight: $regular;
  color: $primary_color;
  cursor: text;
  padding: 0 !important;
  box-sizing: border-box;
  border: 0;
}

.maz-input:focus-within {
  border-color: $primary_color !important;
}

.error {
  border-color: $error_color !important;
}

.success {
  border-color: $success-color !important;
}

.maz-input {
  border-color: $primary_color;
  height: auto;
  padding: 10px 14px;
  box-sizing: content-box;
  min-height: initial;
  outline: none;
  transition: none;

  &:focus-within {
    border-width: 2px;
  }
}

.password-icon {
  fill: $primary-color;
}
</style>
